import { ReactNode, useId } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import CloseButton from './CloseButton';
import Button from './Button';
import { MODAL_OVERLAY_CLASS, MODAL_PANEL_CLASS, MODAL_OVERLAY_STYLE, useDialog } from './Modal';
import { TEXT } from './Typography';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning';
  isLoading?: boolean;
  confirmDisabled?: boolean;
}

export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false,
  confirmDisabled = false,
}: ConfirmationModalProps) {
  // Focus, focus trap, scroll lock and Escape, shared with Modal -- including
  // the open-dialog stack, so Escape on a confirm over a Modal closes only the
  // confirm. Nothing dismisses it while the action runs.
  const titleId = useId();
  const { panelRef, trapFocus } = useDialog({ isOpen, onClose, dismissible: !isLoading });

  if (!isOpen) return null;

  const accentColor = variant === 'danger' ? 'bg-red-600' : 'bg-amber-600';
  // The icon has to follow the variant too. It was hardcoded amber, so a
  // `danger` confirm rendered a red bar and a red button either side of an
  // amber warning triangle.
  const iconColor = variant === 'danger' ? 'text-red-600' : 'text-amber-600';

  return (
    <div
      className={`z-[100] ${MODAL_OVERLAY_CLASS}`}
      style={MODAL_OVERLAY_STYLE}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={trapFocus}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`${MODAL_PANEL_CLASS} relative w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200`}
        onClick={e => e.stopPropagation()}
      >
        {/* Accent bar at top */}
        <div className={`h-1 ${accentColor}`} />

        {/* Close button */}
        <CloseButton
          onClick={onClose}
          disabled={isLoading}
          position="absolute"
          label="Close confirmation"
        />

        {/* Content */}
        <div className="p-6">
          {/* Header with logo */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
            <img
              src="/grautomates-dark.png"
              alt="GRPS"
              className="h-8 w-auto"
            />
            <div className="h-6 w-px bg-gray-200" />
            <span className={TEXT.detailLabel}>Confirmation Required</span>
          </div>

          {/* Icon and Title */}
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className={`h-6 w-6 ${iconColor}`} aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <h3
                id={titleId}
                className={TEXT.sectionTitle}
              >
                {title}
              </h3>
              <div className={`mt-1 leading-relaxed ${TEXT.bodyMuted}`}>{message}</div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-col-reverse sm:flex-row items-center justify-end gap-2 sm:gap-3">
            <Button
              variant="outline"
              fullWidth
              onClick={onClose}
              disabled={isLoading}
              className="sm:w-auto"
            >
              {cancelText}
            </Button>
            <Button
              variant={variant}
              fullWidth
              onClick={onConfirm}
              disabled={isLoading || confirmDisabled}
              className="sm:w-auto"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </span>
              ) : confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
